"use client";

import { Popover as ArkPopover } from "@ark-ui/react/popover";
import { Portal } from "@ark-ui/react/portal";
import { Fragment, JSX, ReactNode } from "react";
import { css, cx } from "styled-system/css";

import { Placement } from "@/types/ui";

export interface PopoverTriggerProps {
  /** Whether the popover is currently open. */
  isOpen: boolean;
  /** A function for setting the `isOpen` state of the popover. */
  setIsOpen: (isOpen: boolean) => void;
}

export interface PopoverProps {
  /**
   * The element that should open the popover when clicked.
   * The element should be an HTML `button` that can accept a ref.
   * You may need to wrap your component in React.forwardRef.
   *
   * Additionally, you can pass a function that returns an element if you wish
   * too access the current `isOpen` state of the popover.
   */
  trigger: JSX.Element | ((renderProps: PopoverTriggerProps) => JSX.Element);
  /**
   * The content of the popover.
   *
   * You can pass a function that returns an element if you wish
   * too access the current `isOpen` state of the popover
   */
  children: ReactNode | ((renderProps: PopoverTriggerProps) => JSX.Element);
  /** Where the popover should be placed relative to the trigger element */
  placement?: Placement;
  /** Whether to make the popover the same width as its trigger. */
  sameWidth?: boolean;
  /** The offset of the popover relative to the trigger element */
  offset?: { mainAxis?: number; crossAxis?: number };
  /** Whether to render the popover in a portal attached to the document body */
  portalled?: boolean;
  /**
   * When `true`, the popover content will only be mounted when the popover is opened
   * and will unmount when the popover is closed.
   *
   * This is useful as a performance optimization for popovers that have a lot of content or
   * content that is expensive to render.
   */
  mountOnlyWhenVisible?: boolean;
  /** Class name for applying additional styles to the popover */
  className?: string;
  /** Whether to make the popover a modal. */
  modal?: boolean;
  /**
   * Clamp the popover to the viewport. Sets `max-width: var(--available-width)`
   * and `max-height: var(--available-height)` on the positioner, and exposes
   * those CSS variables to children so the content can be made scrollable.
   */
  fitViewport?: boolean;
  /** Controlled open state. When provided, the popover is fully controlled by the parent. */
  open?: boolean;
  /** Called when the popover open state changes (controlled mode). */
  onOpenChange?: (details: { open: boolean }) => void;
}

/** A component for opening a popover that appears adjacent to the trigger element provided. */
export function Popover({
  trigger,
  children,
  placement,
  sameWidth = false,
  offset,
  portalled = true,
  mountOnlyWhenVisible = false,
  className,
  modal,
  fitViewport,
  open,
  onOpenChange,
}: PopoverProps) {
  const PopoverWrapper = portalled ? Portal : Fragment;

  return (
    <ArkPopover.Root
      positioning={{ placement, sameWidth, offset, fitViewport }}
      portalled={portalled}
      lazyMount={mountOnlyWhenVisible}
      unmountOnExit={mountOnlyWhenVisible}
      modal={modal}
      open={open}
      onOpenChange={onOpenChange}
    >
      <ArkPopover.Context>
        {(api) => (
          <>
            <ArkPopover.Trigger asChild>
              {typeof trigger === "function"
                ? trigger({ isOpen: api.open, setIsOpen: api.setOpen })
                : trigger}
            </ArkPopover.Trigger>
            <PopoverWrapper>
              <ArkPopover.Positioner>
                <ArkPopover.Content className={cx(contentStyles, className)}>
                  {typeof children === "function"
                    ? children({ isOpen: api.open, setIsOpen: api.setOpen })
                    : children}
                </ArkPopover.Content>
              </ArkPopover.Positioner>
            </PopoverWrapper>
          </>
        )}
      </ArkPopover.Context>
    </ArkPopover.Root>
  );
}

const contentStyles = css({
  zIndex: "popover",
  _open: { animationStyle: "fade-in" },
  _closed: { animationStyle: "fade-out" },
});
